import { useState, useEffect } from 'react';
import {
  Clock,
  CheckCircle2,
  Play,
  User,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { yardApi, YardPutawayTask, YardStorageLocation } from '@/api/services/yard.api';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';

type PutawayStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export default function YardPutaway() {
  const [tasks, setTasks] = useState<YardPutawayTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completedToday: 0,
    avgCompletionTime: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  // Selected task for details
  const [selectedTask, setSelectedTask] = useState<YardPutawayTask | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Complete dialog
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<YardPutawayTask | null>(null);
  const [completeForm, setCompleteForm] = useState({
    storageLocationId: '',
    notes: '',
  });
  const [storageLocations, setStorageLocations] = useState<YardStorageLocation[]>([]);

  // Suggested location
  const [suggestedLocation, setSuggestedLocation] = useState<YardStorageLocation | null>(null);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [statusFilter, priorityFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;

      const response = await yardApi.putaway.getAll(params);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching putaway tasks:', error);
      toast.error('Failed to load putaway tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await yardApi.putaway.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchStorageLocations = async () => {
    try {
      const response = await yardApi.storageLocations.getAll({ status: 'AVAILABLE' });
      setStorageLocations(response.data);
    } catch (error) {
      console.error('Error fetching storage locations:', error);
    }
  };

  const handleStartTask = async (task: YardPutawayTask) => {
    try {
      await yardApi.putaway.start(task.id);
      toast.success('Putaway task started');
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error('Failed to start task');
    }
  };

  const handleOpenCompleteDialog = async (task: YardPutawayTask) => {
    setTaskToComplete(task);
    setCompleteForm({
      storageLocationId: task.storageLocationId || '',
      notes: '',
    });

    // Fetch storage locations and suggested location
    await fetchStorageLocations();

    if (task.item?.id) {
      try {
        const response = await yardApi.storageLocations.suggestLocation(task.item.id);
        setSuggestedLocation(response.suggested);
        if (response.suggested?.id) {
          setCompleteForm(prev => ({ ...prev, storageLocationId: response.suggested.id }));
        }
      } catch (error) {
        console.error('Error getting suggested location:', error);
      }
    }

    setCompleteDialogOpen(true);
  };

  const handleCompleteTask = async () => {
    if (!taskToComplete) return;

    if (!completeForm.storageLocationId) {
      toast.error('Please select a storage location');
      return;
    }

    try {
      await yardApi.putaway.complete(taskToComplete.id, {
        storageLocationId: completeForm.storageLocationId,
        putawayQty: taskToComplete.quantity,
        notes: completeForm.notes || undefined,
      });
      toast.success('Putaway task completed');
      setCompleteDialogOpen(false);
      setTaskToComplete(null);
      setSuggestedLocation(null);
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  const handleViewDetails = (task: YardPutawayTask) => {
    setSelectedTask(task);
    setIsSheetOpen(true);
  };

  const getStatusBadge = (status: PutawayStatus) => {
    const config: Record<PutawayStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      PENDING: { variant: 'outline', label: 'Pending' },
      IN_PROGRESS: { variant: 'default', label: 'In Progress' },
      COMPLETED: { variant: 'secondary', label: 'Completed' },
      CANCELLED: { variant: 'destructive', label: 'Cancelled' },
    };
    const { variant, label } = config[status] || { variant: 'secondary', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getPriorityBadge = (priority: number) => {
    if (priority <= 3) return <Badge variant="destructive">High</Badge>;
    if (priority <= 6) return <Badge variant="outline">Medium</Badge>;
    return <Badge variant="secondary">Low</Badge>;
  };

  const filteredTasks = tasks.filter(task => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTaskNumber = task.taskNumber?.toLowerCase().includes(query);
      const matchesItem = task.item?.name?.toLowerCase().includes(query);
      const matchesSku = task.item?.sku?.toLowerCase().includes(query);
      if (!matchesTaskNumber && !matchesItem && !matchesSku) return false;
    }
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Putaway Tasks"
        description="Manage warehouse putaway operations"
        actions={
          <Button variant="outline" onClick={() => { fetchTasks(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Pending Tasks"
          value={stats.pending}
          icon={<Clock className="h-5 w-5" />}
        />
        <MetricCard
          label="In Progress"
          value={stats.inProgress}
          icon={<Play className="h-5 w-5" />}
        />
        <MetricCard
          label="Completed Today"
          value={stats.completedToday}
          icon={<CheckCircle2 className="h-5 w-5" />}
        />
        <MetricCard
          label="Avg Completion Time"
          value={`${stats.avgCompletionTime} min`}
          icon={<Clock className="h-5 w-5" />}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by task number, item name, or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="high">High (1-3)</SelectItem>
              <SelectItem value="medium">Medium (4-6)</SelectItem>
              <SelectItem value="low">Low (7-10)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tasks Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task #</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Loading tasks...
                  </TableCell>
                </TableRow>
              ) : filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No putaway tasks found
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell className="font-medium">{task.taskNumber}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{task.item?.name || 'Unknown Item'}</p>
                        <p className="text-sm text-muted-foreground">{task.item?.sku}</p>
                      </div>
                    </TableCell>
                    <TableCell>{task.quantity} / {task.putawayQty} put away</TableCell>
                    <TableCell>
                      <Badge variant="outline">{task.sourceType}</Badge>
                    </TableCell>
                    <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                    <TableCell>{getStatusBadge(task.status as PutawayStatus)}</TableCell>
                    <TableCell>
                      {task.assignedTo ? (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{task.assignedTo.fullName}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(task)}
                        >
                          View
                        </Button>
                        {task.status === 'PENDING' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartTask(task)}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Start
                          </Button>
                        )}
                        {task.status === 'IN_PROGRESS' && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleOpenCompleteDialog(task)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Complete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Task Details Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Task Details</SheetTitle>
            <SheetDescription>
              Putaway task information and status
            </SheetDescription>
          </SheetHeader>
          {selectedTask && (
            <div className="mt-6 space-y-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Task Number</span>
                  <span className="font-medium">{selectedTask.taskNumber}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Status</span>
                  {getStatusBadge(selectedTask.status as PutawayStatus)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Priority</span>
                  {getPriorityBadge(selectedTask.priority)}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Item Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Name</span>
                    <span>{selectedTask.item?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">SKU</span>
                    <span>{selectedTask.item?.sku}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Quantity</span>
                    <span>{selectedTask.quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Put Away Qty</span>
                    <span>{selectedTask.putawayQty}</span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Source Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Source Type</span>
                    <Badge variant="outline">{selectedTask.sourceType}</Badge>
                  </div>
                  {selectedTask.sourceId && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Source ID</span>
                      <span className="text-sm">{selectedTask.sourceId}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedTask.storageLocation && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Storage Location</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Code</span>
                      <span>{selectedTask.storageLocation.code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Name</span>
                      <span>{selectedTask.storageLocation.name}</span>
                    </div>
                  </div>
                </div>
              )}

              {selectedTask.notes && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedTask.notes}</p>
                </div>
              )}

              <div className="border-t pt-4 flex gap-2">
                {selectedTask.status === 'PENDING' && (
                  <Button
                    className="flex-1"
                    onClick={() => {
                      handleStartTask(selectedTask);
                      setIsSheetOpen(false);
                    }}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Start Task
                  </Button>
                )}
                {selectedTask.status === 'IN_PROGRESS' && (
                  <Button
                    className="flex-1"
                    onClick={() => {
                      handleOpenCompleteDialog(selectedTask);
                      setIsSheetOpen(false);
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Complete Task
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Complete Task Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Putaway Task</DialogTitle>
            <DialogDescription>
              Select the storage location where the items will be placed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {suggestedLocation && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Suggested Location
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  {suggestedLocation.code} - {suggestedLocation.name}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="storageLocation">Storage Location *</Label>
              <Select
                value={completeForm.storageLocationId}
                onValueChange={(value) =>
                  setCompleteForm((prev) => ({ ...prev, storageLocationId: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select storage location" />
                </SelectTrigger>
                <SelectContent>
                  {storageLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.code} - {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any notes about this putaway..."
                value={completeForm.notes}
                onChange={(e) =>
                  setCompleteForm((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCompleteTask}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Complete Putaway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
